import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChefHat, Camera, FileText, MessageSquare, Pencil, Plus, Trash2, BarChart3, Settings2, ClipboardList, MessageCircle } from 'lucide-react';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/** v0.3.x (AIトークン利用上限管理、F-3): 利用上限管理パネル。
 *  設計: docs/design/v03-ai-token-quota.md §7.2 (段階分割ワイヤ) / detail §4.2, §14a.2
 *  MVPスコープ: scope=tenant/node/user を UI 対応 (store scope は既存店舗運用が node に統合済みのため
 *  UI からは省略、CLAUDE.md §6d 過剰実装回避)。 */

type LimitRow = {
  limitId: string;
  scope: 'tenant' | 'node' | 'store' | 'user';
  scopeTargetId: string | null;
  nodeId: string | null;
  costUsdLimit: number | null;
  tokenLimit: number | null;
  enforcementMode: 'hard' | 'soft';
  effectiveFrom: string;
  updatedAt: string;
};

type PreviewEstimate = {
  feature: string;
  avgCostPerCall: number;
  estimatedCalls: number;
  basis: 'actual30d' | 'static';
  sampleSize: number;
};

// v0.5⑧ 増分 経営会議ブリーフ対話AI (§8 co-change #5/#7): board_chat 追加 + 既存の欠落分
// (analysis/ai_setup/board_brief) を補完。
const FEATURE_LABELS: Record<string, string> = {
  menu_dev: 'AI メニュー開発',
  ocr: 'AI-OCR (納品書1枚)',
  auto_comment: 'AI 自動コメント',
  daily_report_ocr: '日報 OCR (写真添付)',
  analysis: 'AI 分析',
  ai_setup: 'AI セットアップ',
  board_brief: 'AI 経営会議ブリーフ',
  board_chat: 'AI 経営会議対話',
};
const FEATURE_ICONS: Record<string, typeof ChefHat> = {
  menu_dev: ChefHat,
  ocr: FileText,
  auto_comment: MessageSquare,
  daily_report_ocr: Camera,
  analysis: BarChart3,
  ai_setup: Settings2,
  board_brief: ClipboardList,
  board_chat: MessageCircle,
};

type FormState = {
  scope: 'tenant' | 'node' | 'user';
  nodeId: string;
  scopeTargetId: string;
  costUsdLimit: string;
  enforcementMode: 'hard' | 'soft';
};

const emptyForm: FormState = { scope: 'tenant', nodeId: '', scopeTargetId: '', costUsdLimit: '', enforcementMode: 'hard' };

export function AiUsageLimitsPanel() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LimitRow | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<LimitRow | null>(null);

  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'), staleTime: 5 * 60 * 1000,
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const nodeNameOf = (id: string | null) => flatNodes.find((n) => n.node.node_id === id)?.node.name ?? id;

  const { data: usersData } = useQuery<Array<{ user_id: string; display_name: string }>>({
    queryKey: ['users-for-ai-usage'],
    queryFn: () => apiFetch<Array<{ user_id: string; display_name: string }>>('/users'),
    enabled: modalOpen && form.scope === 'user',
  });
  const userNameOf = (id: string | null) => usersData?.find((u) => u.user_id === id)?.display_name ?? id;

  const { data: limitsData, isLoading } = useQuery<{ limits: LimitRow[] }>({
    queryKey: ['ai-usage-limits'],
    queryFn: () => apiFetch<{ limits: LimitRow[] }>('/ai-usage/limits'),
  });
  const limits = limitsData?.limits ?? [];

  // F-4a: preview を debounce 300ms で取得
  const [preview, setPreview] = useState<PreviewEstimate[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  useEffect(() => {
    const amount = Number(form.costUsdLimit);
    if (!modalOpen || !amount || amount <= 0) { setPreview(null); return; }
    if (form.scope === 'node' && !form.nodeId) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch<{ estimates: PreviewEstimate[] }>('/ai-usage/limits/preview', {
          method: 'POST',
          body: JSON.stringify({ scope: form.scope, nodeId: form.scope === 'node' ? form.nodeId : undefined, costUsdLimit: amount }),
        });
        setPreview(res.estimates);
        setPreviewError(null);
      } catch (e) {
        setPreview(null);
        setPreviewError(e instanceof ApiClientError ? e.message : '目安の取得に失敗しました');
      }
    }, 300);
    return () => clearTimeout(t);
  }, [modalOpen, form.scope, form.nodeId, form.costUsdLimit]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setPreview(null); setModalOpen(true); };
  const openEdit = (row: LimitRow) => {
    setEditing(row);
    setForm({
      scope: row.scope === 'store' ? 'tenant' : row.scope,
      nodeId: row.nodeId ?? '',
      scopeTargetId: row.scopeTargetId ?? '',
      costUsdLimit: row.costUsdLimit != null ? String(row.costUsdLimit) : '',
      enforcementMode: row.enforcementMode,
    });
    setPreview(null);
    setModalOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        scope: form.scope,
        nodeId: form.scope === 'node' ? form.nodeId : undefined,
        scopeTargetId: form.scope === 'user' ? form.scopeTargetId : undefined,
        costUsdLimit: Number(form.costUsdLimit),
        enforcementMode: form.enforcementMode,
      };
      return editing
        ? apiFetch(`/ai-usage/limits/${editing.limitId}`, { method: 'PUT', body: JSON.stringify(body) })
        : apiFetch('/ai-usage/limits', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      toast.success('保存しました');
      qc.invalidateQueries({ queryKey: ['ai-usage-limits'] });
      setModalOpen(false);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/ai-usage/limits/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('削除しました');
      qc.invalidateQueries({ queryKey: ['ai-usage-limits'] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const scopeLabel = (row: LimitRow): string => {
    if (row.scope === 'tenant') return 'テナント全体';
    if (row.scope === 'node') return `ノード: ${nodeNameOf(row.nodeId)}`;
    if (row.scope === 'user') return `個人: ${userNameOf(row.scopeTargetId)}`;
    return `店舗: ${row.scopeTargetId}`;
  };

  const canSave = form.costUsdLimit !== '' && Number(form.costUsdLimit) > 0
    && (form.scope !== 'node' || !!form.nodeId)
    && (form.scope !== 'user' || !!form.scopeTargetId);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-2 rounded bg-brand-red text-white text-sm font-semibold hover:bg-brand-red-hover">
          <Plus size={16} /> 新規上限設定
        </button>
      </div>

      <div className="border border-surface-line rounded-lg bg-surface-card overflow-x-auto">
        {isLoading && <p className="p-4 text-sm text-text-sub">読み込み中…</p>}
        {!isLoading && limits.length === 0 && (
          <p className="p-4 text-sm text-text-sub">上限設定はまだありません。「新規上限設定」から作成できます。</p>
        )}
        {limits.length > 0 && (
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-surface-line text-left text-text-sub">
                <th scope="col" className="py-2 px-4 font-medium">対象</th>
                <th scope="col" className="py-2 px-4 font-medium text-right">月次上限</th>
                <th scope="col" className="py-2 px-4 font-medium">Enforcement</th>
                <th scope="col" className="py-2 px-4 font-medium" />
              </tr>
            </thead>
            <tbody>
              {limits.map((row) => (
                <tr key={row.limitId} className="border-b border-surface-line last:border-0">
                  <td className="py-2 px-4">{scopeLabel(row)}</td>
                  <td className="py-2 px-4 text-right tabular-nums">{row.costUsdLimit != null ? `$${row.costUsdLimit.toFixed(2)}` : '—'}</td>
                  <td className="py-2 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${row.enforcementMode === 'hard' ? 'bg-danger-bg text-danger-text' : 'bg-alert-bg text-alert-text'}`}>
                      {row.enforcementMode === 'hard' ? 'Hard (拒否)' : 'Soft (警告のみ)'}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => openEdit(row)} aria-label="編集"
                        className="p-1.5 rounded hover:bg-surface-sunken text-text-sub hover:text-text-main min-w-[44px] min-h-[44px] flex items-center justify-center sm:min-w-0 sm:min-h-0">
                        <Pencil size={14} />
                      </button>
                      <button type="button" onClick={() => setDeleteTarget(row)} aria-label="削除"
                        className="p-1.5 rounded hover:bg-danger-bg text-text-sub hover:text-danger-text min-w-[44px] min-h-[44px] flex items-center justify-center sm:min-w-0 sm:min-h-0">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? '上限設定を編集' : '新規上限設定'}
        size="md"
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)}
              className="h-10 px-5 rounded-md bg-surface-card text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken min-h-[44px] sm:min-h-[40px]">
              キャンセル
            </button>
            <button type="button" onClick={() => saveMut.mutate()} disabled={!canSave || saveMut.isPending}
              className="h-10 px-5 rounded-md text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover disabled:opacity-45 disabled:cursor-not-allowed min-h-[44px] sm:min-h-[40px] bg-brand-red">
              {saveMut.isPending ? '保存中…' : '保存'}
            </button>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <div>
            <span className="block text-xs text-text-sub mb-1">対象</span>
            <div className="flex gap-2">
              {(['tenant', 'node', 'user'] as const).map((s) => (
                <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, scope: s }))}
                  disabled={!!editing}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    form.scope === s ? 'bg-brand-red text-white border-brand-red' : 'border-surface-line text-text-sub'
                  } disabled:opacity-50`}>
                  {s === 'tenant' ? 'テナント全体' : s === 'node' ? '組織ノード' : '個人'}
                </button>
              ))}
            </div>
          </div>

          {form.scope === 'node' && (
            <div>
              <span className="block text-xs text-text-sub mb-1" id="limit-node-label">ノード*</span>
              <Select value={form.nodeId} onValueChange={(v) => setForm((f) => ({ ...f, nodeId: v }))} disabled={!!editing}>
                <SelectTrigger aria-labelledby="limit-node-label">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {flatNodes.map(({ node, depth }) => (
                    <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.scope === 'user' && (
            <div>
              <span className="block text-xs text-text-sub mb-1" id="limit-user-label">ユーザー*</span>
              <Select value={form.scopeTargetId} onValueChange={(v) => setForm((f) => ({ ...f, scopeTargetId: v }))} disabled={!!editing}>
                <SelectTrigger aria-labelledby="limit-user-label">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {(usersData ?? []).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label htmlFor="limit-cost" className="block text-xs text-text-sub mb-1">月次上限 (USD)*</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub">$</span>
              <input id="limit-cost" type="text" inputMode="decimal" value={form.costUsdLimit}
                onChange={(e) => {
                  const v = e.target.value;
                  // 数値スピナーによる誤操作を避けるため text+inputMode に変更 (Design C-1)。
                  // 半角数字と小数点1つのみ許容 (§ ui-guidelines コード列バリデーションと同様の考え方)
                  if (v === '' || /^\d*\.?\d*$/.test(v)) setForm((f) => ({ ...f, costUsdLimit: v }));
                }}
                className="w-full pl-6 pr-3 py-2 rounded border border-surface-line focus-ring" />
            </div>
          </div>

          <div>
            <span className="block text-xs text-text-sub mb-1">Enforcement</span>
            <div className="flex gap-4">
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" name="enforcement" checked={form.enforcementMode === 'hard'}
                  onChange={() => setForm((f) => ({ ...f, enforcementMode: 'hard' }))} />
                Hard (拒否)
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" name="enforcement" checked={form.enforcementMode === 'soft'}
                  onChange={() => setForm((f) => ({ ...f, enforcementMode: 'soft' }))} />
                Soft (警告のみ)
              </label>
            </div>
          </div>

          {(preview || previewError) && (
            <div className="rounded-md bg-surface-sunken p-3 space-y-1.5">
              <div className="text-xs font-semibold text-text-sub">この上限で使える AI 機能の目安</div>
              {previewError && <p className="text-xs text-danger-text">{previewError}</p>}
              {preview?.map((e) => {
                const Icon = FEATURE_ICONS[e.feature] ?? MessageSquare;
                return (
                  <div key={e.feature} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5"><Icon size={13} className="text-text-sub" />{FEATURE_LABELS[e.feature] ?? e.feature}</span>
                    <span className="tabular-nums">
                      約 {e.estimatedCalls.toLocaleString()} 回
                      <span className="text-text-sub ml-1">({e.basis === 'actual30d' ? `実績${e.sampleSize}件` : '参考値'})</span>
                    </span>
                  </div>
                );
              })}
              <p className="text-[11px] text-text-sub pt-1">※実際の消費は入出力量により変動します</p>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="上限設定を削除しますか"
        message={deleteTarget ? `「${scopeLabel(deleteTarget)}」の上限設定を削除します。削除後は上位スコープの上限に従います。` : ''}
        destructive
        confirmLabel="削除"
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.limitId)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
